import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import {
  authAPI,
  communityAPI,
  getUserErrorMessage,
  ResearchInterest,
  ThreadCategory,
  ThreadDetail,
  ThreadListResponse,
  User,
} from '../api/api';
import HeroFlyer from '../components/HeroFlyer';
import '../styles/community.css';

type Tab = 'discussions' | 'research';

const CATEGORIES: { value: ThreadCategory | ''; label: string }[] = [
  { value: '', label: 'All topics' },
  { value: 'discussion', label: 'Discussion' },
  { value: 'meetup', label: 'Meetups' },
  { value: 'research', label: 'Research' },
];

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return value;
  }
}

function categoryLabel(category: string): string {
  const match = CATEGORIES.find((item) => item.value === category);
  return match?.label || category;
}

const Community: React.FC = () => {
  const [tab, setTab] = useState<Tab>('discussions');
  const [me, setMe] = useState<User | null>(null);
  const [category, setCategory] = useState<ThreadCategory | ''>('');
  const [threads, setThreads] = useState<ThreadListResponse | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selected, setSelected] = useState<ThreadDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newCategory, setNewCategory] = useState<ThreadCategory>('discussion');
  const [creating, setCreating] = useState(false);

  const [replyBody, setReplyBody] = useState('');
  const [replying, setReplying] = useState(false);

  const [interest, setInterest] = useState<ResearchInterest | null>(null);
  const [topics, setTopics] = useState('');
  const [availability, setAvailability] = useState('');
  const [consent, setConsent] = useState(true);
  const [savingInterest, setSavingInterest] = useState(false);
  const [loadingInterest, setLoadingInterest] = useState(false);

  const loadThreads = useCallback(() => {
    setLoadingList(true);
    setError('');
    communityAPI
      .listThreads({
        category: category || undefined,
        limit: 30,
        offset: 0,
      })
      .then((response) => setThreads(response))
      .catch((err: unknown) => {
        setError(
          getUserErrorMessage(err, 'Could not load community discussions. Please try again.')
        );
      })
      .finally(() => setLoadingList(false));
  }, [category]);

  useEffect(() => {
    authAPI.getMe().then(setMe).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (tab === 'discussions') {
      loadThreads();
    }
  }, [tab, loadThreads]);

  useEffect(() => {
    if (tab !== 'research') {
      return;
    }
    let active = true;
    setLoadingInterest(true);
    communityAPI
      .getResearchInterest()
      .then((data) => {
        if (!active) return;
        setInterest(data);
        if (data) {
          setTopics(data.topics);
          setAvailability(data.availability_notes || '');
          setConsent(data.consent_contact);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(
            getUserErrorMessage(err, 'Could not load your research interest profile.')
          );
        }
      })
      .finally(() => {
        if (active) setLoadingInterest(false);
      });
    return () => {
      active = false;
    };
  }, [tab]);

  const openThread = (id: number) => {
    setSelectedId(id);
    setSelected(null);
    setReplyBody('');
    setLoadingThread(true);
    setError('');
    communityAPI
      .getThread(id)
      .then(setSelected)
      .catch((err: unknown) => {
        setError(getUserErrorMessage(err, 'Could not open this discussion.'));
        setSelectedId(null);
      })
      .finally(() => setLoadingThread(false));
  };

  const closeThread = () => {
    setSelectedId(null);
    setSelected(null);
    setReplyBody('');
  };

  const handleCreateThread = async (event: FormEvent) => {
    event.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const thread = await communityAPI.createThread({
        title: newTitle.trim(),
        body: newBody.trim(),
        category: newCategory,
      });
      setNewTitle('');
      setNewBody('');
      setNewCategory('discussion');
      setSuccess('Your discussion is live.');
      loadThreads();
      openThread(thread.id);
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not start this discussion.'));
    } finally {
      setCreating(false);
    }
  };

  const handleReply = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedId) return;
    setReplying(true);
    setError('');
    try {
      await communityAPI.reply(selectedId, replyBody.trim());
      setReplyBody('');
      const refreshed = await communityAPI.getThread(selectedId);
      setSelected(refreshed);
      loadThreads();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not post your reply.'));
    } finally {
      setReplying(false);
    }
  };

  const handleDeleteThread = async (id: number) => {
    if (!window.confirm('Delete this discussion and all replies?')) return;
    setError('');
    try {
      await communityAPI.deleteThread(id);
      if (selectedId === id) closeThread();
      setSuccess('Discussion deleted.');
      loadThreads();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not delete this discussion.'));
    }
  };

  const handleDeletePost = async (postId: number) => {
    if (!selectedId || !window.confirm('Delete this reply?')) return;
    setError('');
    try {
      await communityAPI.deletePost(postId);
      const refreshed = await communityAPI.getThread(selectedId);
      setSelected(refreshed);
      loadThreads();
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not delete this reply.'));
    }
  };

  const handleInterestSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSavingInterest(true);
    setError('');
    setSuccess('');
    try {
      const saved = await communityAPI.submitResearchInterest({
        topics: topics.trim(),
        availability_notes: availability.trim() || undefined,
        consent_contact: consent,
      });
      setInterest(saved);
      setSuccess(
        interest
          ? 'Your research interest was updated.'
          : 'You are on the list. We will reach out when a matching study opens.'
      );
    } catch (err: unknown) {
      setError(getUserErrorMessage(err, 'Could not save your research interest.'));
    } finally {
      setSavingInterest(false);
    }
  };

  const canModerate = (authorId: number) =>
    Boolean(me && (me.id === authorId || me.is_admin));

  return (
    <motion.main
      className="container community-page"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <section className="card community-intro">
        <p className="community-eyebrow">Gather · Discuss · Contribute</p>
        <h1>Research Groups</h1>
        <p>
          Meet fellow seekers, talk about psychedelics with care, and raise your
          hand to join WisemenResearch study efforts.
        </p>
      </section>

      <div className="community-tabs" role="tablist" aria-label="Community sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'discussions'}
          className={`community-tab ${tab === 'discussions' ? 'active' : ''}`}
          onClick={() => {
            setTab('discussions');
            setError('');
            setSuccess('');
          }}
        >
          Discussions
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'research'}
          className={`community-tab ${tab === 'research' ? 'active' : ''}`}
          onClick={() => {
            setTab('research');
            setError('');
            setSuccess('');
            closeThread();
          }}
        >
          Join research
        </button>
      </div>

      {error && (
        <div className="error community-banner" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="community-success" role="status">
          {success}
        </div>
      )}

      {tab === 'discussions' && !selectedId && (
        <>
          <section className="card community-compose" aria-label="Start a discussion">
            <h2>Start a conversation</h2>
            <form className="community-form" onSubmit={handleCreateThread}>
              <label className="form-group">
                <span>Title</span>
                <input
                  className="form-input"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  minLength={3}
                  maxLength={200}
                  required
                  placeholder="What do you want to explore?"
                />
              </label>
              <label className="form-group">
                <span>Category</span>
                <select
                  className="form-input"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as ThreadCategory)}
                >
                  <option value="discussion">Discussion</option>
                  <option value="meetup">Meetup</option>
                  <option value="research">Research</option>
                </select>
              </label>
              <label className="form-group community-form-full">
                <span>Message</span>
                <textarea
                  className="form-input community-textarea"
                  value={newBody}
                  onChange={(e) => setNewBody(e.target.value)}
                  minLength={10}
                  maxLength={10000}
                  required
                  rows={5}
                  placeholder="Share context, questions, or an invitation to meet. Keep it educational and responsible."
                />
              </label>
              <button className="btn btn-primary" type="submit" disabled={creating}>
                {creating ? 'Posting…' : 'Post discussion'}
              </button>
            </form>
          </section>

          <section className="community-list-header" aria-label="Filter discussions">
            <label className="community-filter">
              <span>Filter</span>
              <select
                className="form-input"
                value={category}
                onChange={(e) => setCategory(e.target.value as ThreadCategory | '')}
              >
                {CATEGORIES.map((item) => (
                  <option key={item.label} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <p className="community-count">
              {threads ? `${threads.total} discussion${threads.total === 1 ? '' : 's'}` : '—'}
            </p>
          </section>

          {loadingList ? (
            <p className="community-muted">Loading discussions…</p>
          ) : !threads || threads.items.length === 0 ? (
            <section className="card community-empty">
              <p>No discussions yet. Be the first to open the floor.</p>
            </section>
          ) : (
            <ul className="community-thread-list">
              {threads.items.map((thread) => (
                <li key={thread.id}>
                  <button
                    type="button"
                    className="card community-thread-card"
                    onClick={() => openThread(thread.id)}
                  >
                    <div className="community-thread-meta">
                      <span className="community-chip">{categoryLabel(thread.category)}</span>
                      <span>{thread.post_count} replies</span>
                    </div>
                    <h3>{thread.title}</h3>
                    <p className="community-preview">{thread.body}</p>
                    <div className="community-thread-foot">
                      <span>{thread.author.name}</span>
                      <time dateTime={thread.created_at}>{formatDate(thread.created_at)}</time>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {tab === 'discussions' && selectedId && (
        <section className="community-thread-view" aria-live="polite">
          <button type="button" className="community-back" onClick={closeThread}>
            ← All discussions
          </button>

          {loadingThread || !selected ? (
            <p className="community-muted">Opening discussion…</p>
          ) : (
            <>
              <article className="card community-thread-detail">
                <div className="community-thread-meta">
                  <span className="community-chip">{categoryLabel(selected.category)}</span>
                  {canModerate(selected.author.id) && (
                    <button
                      type="button"
                      className="community-danger"
                      onClick={() => handleDeleteThread(selected.id)}
                    >
                      Delete thread
                    </button>
                  )}
                </div>
                <h2>{selected.title}</h2>
                <p className="community-body">{selected.body}</p>
                <div className="community-thread-foot">
                  <span>{selected.author.name}</span>
                  <time dateTime={selected.created_at}>{formatDate(selected.created_at)}</time>
                </div>
              </article>

              <div className="community-replies">
                <h3>Replies ({selected.posts.length})</h3>
                {selected.posts.length === 0 ? (
                  <p className="community-muted">No replies yet. Start the exchange.</p>
                ) : (
                  <ul className="community-reply-list">
                    {selected.posts.map((post) => (
                      <li key={post.id} className="card community-reply">
                        <div className="community-thread-foot">
                          <span>{post.author.name}</span>
                          <time dateTime={post.created_at}>{formatDate(post.created_at)}</time>
                        </div>
                        <p className="community-body">{post.body}</p>
                        {canModerate(post.author.id) && (
                          <button
                            type="button"
                            className="community-danger"
                            onClick={() => handleDeletePost(post.id)}
                          >
                            Delete
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <form className="card community-compose" onSubmit={handleReply}>
                <h3>Reply</h3>
                <label className="form-group">
                  <span className="sr-only">Your reply</span>
                  <textarea
                    className="form-input community-textarea"
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    required
                    maxLength={5000}
                    rows={4}
                    placeholder="Add your thoughts…"
                  />
                </label>
                <button className="btn btn-primary" type="submit" disabled={replying}>
                  {replying ? 'Sending…' : 'Post reply'}
                </button>
              </form>
            </>
          )}
        </section>
      )}

      {tab === 'research' && (
        <>
          <HeroFlyer>
            <p className="community-flyer-caption">
              Paid and volunteer candidates wanted for psychedelic research studies.
              Share your interests below to join the Wisemen Research Group list.
            </p>
          </HeroFlyer>
          <section className="card community-research" aria-label="Join research">
          <h2>Become part of the research</h2>
          <p>
            Tell us what you want to study or contribute to. This is an interest
            list — not enrollment in a clinical trial. We will only contact you
            if you consent below.
          </p>

          {loadingInterest ? (
            <p className="community-muted">Loading your profile…</p>
          ) : (
            <>
              {interest && (
                <p className="community-interest-status">
                  Status: <strong>{interest.status}</strong>
                  {interest.updated_at
                    ? ` · updated ${formatDate(interest.updated_at)}`
                    : ` · submitted ${formatDate(interest.created_at)}`}
                </p>
              )}

              <form className="community-form community-form-stack" onSubmit={handleInterestSubmit}>
                <label className="form-group">
                  <span>Topics or study areas</span>
                  <textarea
                    className="form-input community-textarea"
                    value={topics}
                    onChange={(e) => setTopics(e.target.value)}
                    required
                    minLength={3}
                    maxLength={2000}
                    rows={4}
                    placeholder="e.g. mystical experience measures, microdosing surveys, harm-reduction education…"
                  />
                </label>
                <label className="form-group">
                  <span>Availability or notes (optional)</span>
                  <textarea
                    className="form-input community-textarea"
                    value={availability}
                    onChange={(e) => setAvailability(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Timezone, preferred formats, prior research experience…"
                  />
                </label>
                <label className="community-consent">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  <span>
                    I consent to be contacted about matching research opportunities
                    using my account email.
                  </span>
                </label>
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={savingInterest || !consent}
                >
                  {savingInterest
                    ? 'Saving…'
                    : interest
                      ? 'Update interest'
                      : 'Join research list'}
                </button>
              </form>
            </>
          )}
        </section>
        </>
      )}
    </motion.main>
  );
};

export default Community;
